// experimentEdit.ts — the two rules the edit dialog needs and the pane
// doesn't: what a slug may look like, and which experiments a given one is
// allowed to be moved under.
//
// Both exist here rather than inside ExperimentForm because both are pure
// functions over the experiment list, both mirror a server-side rule
// (SlugifyBranch's alphabet; ReparentExperiment's cycle check), and a rule
// that mirrors the server is worth reading on its own.
import type { Experiment } from '../api';

// The slug alphabet the server produces and accepts: lowercase letters and
// digits in '-'-separated runs. No slashes — a branch name's slashes are
// collapsed on the way in, and the slug is a path segment in every
// experiment URL — no leading, trailing or doubled '-', and nothing
// uppercase.
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

// A slug long enough to break the pane's layout is refused here rather
// than by a database column limit nobody can see.
export const SLUG_MAX = 64;

// Why this slug is unacceptable, or '' when it is fine. The message is
// shown against the field: a silent auto-correction would rename the
// experiment to something the user never typed.
export function slugError(slug: string): string {
  const s = slug.trim();
  if (!s) return 'A name is required.';
  if (s.length > SLUG_MAX) return `At most ${SLUG_MAX} characters.`;
  if (s !== slug) return 'No leading or trailing spaces.';
  if (/\//.test(s)) return 'No slashes — a name is one path segment.';
  if (/[A-Z]/.test(s)) return 'Lowercase only.';
  if (!SLUG_PATTERN.test(s)) {
    return 'Lowercase letters, digits and single dashes between them.';
  }
  return '';
}

// Every experiment reachable downwards from `rootId`, including it. The
// server answers a reparent into this set with 409 (db.ErrCycle), so the
// picker leaves the whole set out instead of offering a move that cannot
// happen.
export function descendantIds(experiments: Experiment[], rootId: number): Set<number> {
  const children = new Map<number, Experiment[]>();
  for (const e of experiments) {
    if (!e.parent_id) continue;
    const arr = children.get(e.parent_id) ?? [];
    arr.push(e);
    children.set(e.parent_id, arr);
  }
  const out = new Set<number>([rootId]);
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop() as number;
    for (const c of children.get(id) ?? []) {
      // A cyclic parent_id in the data must not spin this loop forever.
      if (out.has(c.id)) continue;
      out.add(c.id);
      stack.push(c.id);
    }
  }
  return out;
}

// The experiments `subject` may be moved under, in the order the list
// arrived (which is the order the pane shows).
export function parentOptions(experiments: Experiment[], subject: Experiment): Experiment[] {
  const blocked = descendantIds(experiments, subject.id);
  return experiments.filter((e) => !blocked.has(e.id));
}
